<script lang="ts">
  import { toaster } from '$lib/toaster';
  import { cn } from '$lib/utils';
  import Button from './ui/button.svelte';
  import Input from './ui/input.svelte';
  import { t } from '$lib/i18n';
  import { createMutation } from '@tanstack/svelte-query';
  import { region, type Region } from '$lib/region';
  import Icon from './ui/icon/icon.svelte';
  import { aboveLg } from '$lib/stores/breakpoints';
  import { elevarAnalyticsEvent, ElevarAnalyticsEvents } from '$lib/analytics';
  let className: undefined | string = undefined;
  let value = '';

  export { className as class };
  export let title: string | undefined = undefined;
  export let description: string | undefined = undefined;
  export let placeholder: string | undefined = undefined;
  export let button: string | undefined = undefined;
  export let variant: 'default' | 'dark' = 'default';
  export let size: 'md' | 'sm' = 'md';
  export let onClose: (() => void) | undefined = undefined;

  const mutation = createMutation<
    unknown,
    unknown,
    { email: string; region: Region; custom_source?: string }
  >({
    mutationKey: ['subscribeNewsletter'],
    mutationFn: async (vars) => {
      const res = await fetch('/api/subscribe/', {
        method: 'POST',
        body: JSON.stringify(vars)
      });

      if (!res.ok) throw new Error('Error subscribing to newsletter');

      return res.json();
    },
    onSuccess: () => {
      elevarAnalyticsEvent({
        event: ElevarAnalyticsEvents.SUBSCRIBE,
        lead_type: 'email',
        user_properties: {
          customer_email: value
        }
      });

      value = '';

      toaster({
        type: 'success',
        title: `${$t('newsletter.success.title')}`,
        description: `${$t('newsletter.success.description')}`
      });
    },
    onError: () => {
      toaster({
        type: 'error',
        title: `${$t('newsletter.error.title')}`,
        description: `${$t('newsletter.error.description')}`
      });
    }
  });
</script>

{#if title || description || placeholder || button}
  <div
    class={cn(
      'flex flex-col gap-8 rounded-md border border-primary/4 bg-primary/4 p-4 text-2xl lg:max-w-[443px] justify-between',
      variant === 'dark' && 'bg-primary text-highlight',
      size === 'sm' && 'text-lg gap-6',
      className
    )}
    {...$$restProps}
  >
    <div class="flex gap-10 justify-between">
      <div>
        {#if title}
          <p class={cn('text-2xl/normal font-semibold', size === 'sm' && 'text-lg/normal')}>
            {title}
          </p>
        {/if}
        {#if description}
          <p>{description}</p>
        {/if}
      </div>
      {#if onClose}
        <button
          class="flex h-fit focus-within:outline-none"
          on:click={() => {
            onClose();
          }}
        >
          <Icon size="lg" icon="cross" />
        </button>
      {/if}
    </div>
    {#if placeholder || button}
      <form
        class="flex gap-2"
        on:submit={(e) => {
          e.preventDefault();
          $mutation.mutate({
            email: value,
            region: $region
          });
        }}
      >
        <Input
          type="email"
          bind:value
          placeholder={placeholder || ''}
          class={cn('w-full', size !== 'sm' && 'h-11 [&_input]:h-11')}
          inputClass={cn(
            variant === 'dark' &&
              'text-highlight border border-highlight/14 placeholder:text-highlight/64'
          )}
        />
        <Button
          name="subscribe"
          variant="accent"
          size={size == 'sm' ? 'sm' : 'md'}
          disabled={!value}
          loading={$mutation.isLoading}
          class={cn(size != 'sm' && 'px-3', size == 'sm' && 'px-2')}
        >
          {#if $aboveLg}
            {button}
          {:else}
            <Icon icon="arrow-right" size="lg" />
          {/if}
        </Button>
      </form>
    {/if}
  </div>
{/if}
